import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, User, Linkedin, Mail, ShieldCheck, Heart } from 'lucide-react';
import SEO from '@/components/SEO';
import ZazuLogo from '@/components/ZazuLogo';
import SocialFooterLinks from '@/components/SocialFooterLinks';

/**
 * About page — audit D3.3, D3.5, D2.4.
 * Currently a clean stub. Founder photo, full name, LinkedIn, and "why I built this" story
 * flagged as pending real content from founder.
 */
const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/40">
      <SEO
        title="About Zazu — The Team Behind the Faith-Based Kids Learning App"
        description="Meet the homeschool parents and educators building Zazu. Our story, mission, and why we're making phonics, math, and Bible curriculum available to every family."
        path="/about"
      />

      <header className="p-4 border-b border-slate-100 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-5 h-5" />
            <ZazuLogo size="sm" />
          </Link>
          <div className="flex gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-teal-700 hover:bg-teal-800">Start free</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
            <Heart className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Our story</span>
          </div>
          <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="about-h1">
            Built by homeschool parents. For every family.
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Zazu started because our own kids deserved a phonics, math, and Bible app that wasn't packed with ads, in-app purchases, or content we didn't trust.
          </p>
        </div>

        {/* Founder card — placeholder pending real photo + name + LinkedIn */}
        <Card className="mb-10 border-2 border-dashed border-emerald-200 bg-white" data-testid="about-founder-card">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-32 h-32 rounded-2xl bg-emerald-50 flex items-center justify-center border-2 border-emerald-100 flex-shrink-0">
                <User className="w-14 h-14 text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium mb-2">
                  Founder page — real photo and bio coming soon
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Founder & CEO</h2>
                <p className="text-sm text-slate-500 mb-4">Homeschool parent · Building Zazu in public</p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Zazu is early. We're a small team of homeschool parents and former educators who wanted one app where our kids could do phonics, math, science, and scripture — without ads, without in-app purchases, and without content we couldn't audit ourselves. If you've ever handed a five-year-old a tablet and cringed, we built this for you.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" disabled className="opacity-60" data-testid="about-linkedin-btn">
                    <Linkedin className="w-4 h-4 mr-2" /> LinkedIn (coming soon)
                  </Button>
                  <Link to="/help">
                    <Button variant="outline" size="sm" data-testid="about-contact-btn">
                      <Mail className="w-4 h-4 mr-2" /> Contact us
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why we built it */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Why we built Zazu</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Existing kids' apps either lacked faith content, buried scripture behind a separate subscription, or shipped ads to five-year-olds. We wanted one place: phonics, math, science, and Bible — priced fairly, safe by default, and built for real families.
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">How we're different</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Faith-based curriculum is built in, not bolted on. AI adapts to your child instead of forcing them through a fixed track. And we're COPPA-compliant from day one — no data ever sold, ever.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Trust strip */}
        <Card className="border-emerald-200 bg-emerald-50/40 mb-12">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-4">
            <ShieldCheck className="w-10 h-10 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-900">COPPA-compliant, ad-free, no data sold — ever.</p>
              <p className="text-sm text-slate-600">Parents own their child's data. Export or delete it anytime, from one screen in settings.</p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/register">
            <Button size="lg" className="bg-teal-700 hover:bg-teal-800 h-14 px-8 rounded-full" data-testid="about-cta-btn">
              Start free — no credit card required
            </Button>
          </Link>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-10 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <ZazuLogo size="md" className="[&_span]:text-white" />
          </div>
          <p className="text-sm mb-3">Impacting lives, one syllable at a time.</p>
          <SocialFooterLinks />
          <p className="text-xs mt-4">&copy; {new Date().getFullYear()} Zazu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
